import { useField } from "formik";
import type { ComponentProps } from "react";

import { useFormatMessage } from "@skilitics/localization";

import { useFieldContext } from "../FormFieldContext";
import { FormAutocomplete } from "./FieldAutocomplete";
import { useCommitChange, useFieldLabelAndDescription, useFieldPlaceholder } from "./helpers";

type AutocompleteOption = { label: string; value: string; disabled?: boolean; group?: string };

type AutocompleteProps = Partial<ComponentProps<typeof FormAutocomplete>> & {
  suggestions?: AutocompleteOption[];
};

export const FieldTags = ({
  suggestions,
  placeholder,
  ...props
}: Omit<AutocompleteProps, "label" | "helperText" | "onChange">) => {
  const commitChange = useCommitChange();
  const formatMessage = useFormatMessage(formatStrings);

  const { name } = useFieldContext();
  const [label, helperText] = useFieldLabelAndDescription(name);
  const fieldPlaceholder = useFieldPlaceholder(name);

  const [{ value }, _, { setValue }] = useField<string[]>(name);

  const handleChange: AutocompleteProps["onChange"] = (values) => {
    setValue(typeof values === "string" ? [values] : values)
      .then(() => commitChange())
      .catch(console.warn);
  };

  const options = suggestions?.filter((suggestion) => !value.includes(suggestion.value)) ?? [];

  return (
    <FormAutocomplete
      {...props}
      placeholder={placeholder ?? (fieldPlaceholder || formatMessage("placeholder"))}
      options={options}
      label={label}
      helperText={helperText}
      onChange={handleChange}
      value={value}
      freeSolo={true}
      multiple={true}
      addValueOfTextInputOnBlur={true}
      clearOnBlur={true}
      blurOnSelect={false}
    />
  );
};

const formatStrings = {
  placeholder: {
    en: "Enter a tag and press Enter",
  },
};
